<script>
import { getMediaHubStatistics } from '@/api/mediaHub.js'

export default {
  name: 'MediaHubStatisticsPage',
  data() {
    return {
      loading: false,
      summary: {},
      displayCategoryStats: [],
      sourceCategoryStats: [],
      contentRankings: [],
      topSearchKeywords: [],
      zeroResultKeywords: [],
    }
  },
  mounted() {
    this.fetchStatistics()
  },
  methods: {
    async fetchStatistics() {
      this.loading = true
      try {
        const res = await getMediaHubStatistics()
        if (res.data?.code === 0) {
          const data = res.data.data || {}
          this.summary = data.summary || {}
          this.displayCategoryStats = data.displayCategoryStats || []
          this.sourceCategoryStats = data.sourceCategoryStats || []
          this.contentRankings = data.contentRankings || []
          this.topSearchKeywords = data.topSearchKeywords || []
          this.zeroResultKeywords = data.zeroResultKeywords || []
        }
      } finally {
        this.loading = false
      }
    },
    number(value) {
      return Number(value || 0).toLocaleString()
    },
    formatDate(value) {
      if (!value) return '-'
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">미디어허브 통계</h1>
        <div class="text-body-2 text-grey">노출 콘텐츠 조회수와 통합검색 키워드를 확인합니다.</div>
      </div>
      <v-btn variant="outlined" prepend-icon="mdi-refresh" :loading="loading" @click="fetchStatistics">새로고침</v-btn>
    </div>

    <v-row class="mb-4">
      <v-col cols="12" md="2">
        <v-card rounded="lg" class="stat-card">
          <div class="stat-label">노출 콘텐츠</div>
          <div class="stat-value">{{ number(summary.exposedContentCount) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card rounded="lg" class="stat-card">
          <div class="stat-label">전체 조회수</div>
          <div class="stat-value">{{ number(summary.totalViewCount) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card rounded="lg" class="stat-card">
          <div class="stat-label">조회수 0</div>
          <div class="stat-value">{{ number(summary.zeroViewContentCount) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card rounded="lg" class="stat-card">
          <div class="stat-label">메인 노출</div>
          <div class="stat-value">{{ number(summary.mainExposeContentCount) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card rounded="lg" class="stat-card">
          <div class="stat-label">검색 횟수</div>
          <div class="stat-value">{{ number(summary.searchCount) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card rounded="lg" class="stat-card">
          <div class="stat-label">결과 없음</div>
          <div class="stat-value">{{ number(summary.zeroResultSearchCount) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="6">
        <v-card rounded="lg">
          <v-card-title>콘텐츠 조회수 TOP 10</v-card-title>
          <v-table density="compact">
            <thead>
              <tr>
                <th>콘텐츠</th>
                <th>카테고리</th>
                <th class="text-end">조회수</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in contentRankings" :key="item.homepageContentCode">
                <td>
                  <div class="rank-title">{{ item.title }}</div>
                  <div class="text-caption text-grey">{{ item.mediaSourceName }} · {{ formatDate(item.lastViewedDt) }}</div>
                </td>
                <td>{{ item.displayCategoryName || '-' }}</td>
                <td class="text-end font-weight-bold">{{ number(item.viewCount) }}</td>
              </tr>
              <tr v-if="!contentRankings.length">
                <td colspan="3" class="text-center text-grey py-6">통계 데이터가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card rounded="lg">
          <v-card-title>인기 검색어 TOP 20</v-card-title>
          <v-table density="compact">
            <thead>
              <tr>
                <th>검색어</th>
                <th class="text-end">검색수</th>
                <th class="text-end">평균 결과</th>
                <th>최근 검색</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in topSearchKeywords" :key="item.keyword">
                <td class="font-weight-bold">{{ item.keyword }}</td>
                <td class="text-end">{{ number(item.searchCount) }}</td>
                <td class="text-end">{{ number(item.averageResultCount) }}</td>
                <td>{{ formatDate(item.lastSearchedDt) }}</td>
              </tr>
              <tr v-if="!topSearchKeywords.length">
                <td colspan="4" class="text-center text-grey py-6">검색어 데이터가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card rounded="lg">
          <v-card-title>미디어허브 카테고리별 조회수</v-card-title>
          <v-table density="compact">
            <thead>
              <tr>
                <th>카테고리</th>
                <th class="text-end">콘텐츠</th>
                <th class="text-end">조회수</th>
                <th class="text-end">평균</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in displayCategoryStats" :key="item.categoryCode">
                <td class="font-weight-bold">{{ item.categoryName }}</td>
                <td class="text-end">{{ number(item.contentCount) }}</td>
                <td class="text-end">{{ number(item.viewCount) }}</td>
                <td class="text-end">{{ number(item.averageViewCount) }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card rounded="lg">
          <v-card-title>수집 카테고리별 통계</v-card-title>
          <v-table density="compact">
            <thead>
              <tr>
                <th>수집 카테고리</th>
                <th class="text-end">수집</th>
                <th class="text-end">노출</th>
                <th class="text-end">조회수</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in sourceCategoryStats" :key="item.categoryCode">
                <td>
                  <div class="font-weight-bold">{{ item.categoryName }}</div>
                  <div class="text-caption text-grey">{{ item.sourceType }}</div>
                </td>
                <td class="text-end">{{ number(item.collectedContentCount) }}</td>
                <td class="text-end">{{ number(item.exposedContentCount) }}</td>
                <td class="text-end">{{ number(item.viewCount) }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card rounded="lg">
          <v-card-title>결과 없음 검색어 TOP 20</v-card-title>
          <v-table density="compact">
            <thead>
              <tr>
                <th>검색어</th>
                <th class="text-end">검색수</th>
                <th>최근 검색</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in zeroResultKeywords" :key="item.keyword">
                <td class="font-weight-bold">{{ item.keyword }}</td>
                <td class="text-end">{{ number(item.searchCount) }}</td>
                <td>{{ formatDate(item.lastSearchedDt) }}</td>
              </tr>
              <tr v-if="!zeroResultKeywords.length">
                <td colspan="3" class="text-center text-grey py-6">결과 없음 검색어가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.stat-card {
  padding: 18px;
}

.stat-label {
  color: #9aa7b8;
  font-size: 13px;
  font-weight: 700;
}

.stat-value {
  margin-top: 8px;
  color: #f3f6fb;
  font-size: 26px;
  font-weight: 800;
}

.rank-title {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
}
</style>
